import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { EMAIL, API_BASE, TRADES } from "@/lib/data";
import { EASE } from "@/components/Reveal";

export default function QuoteDialog({ open, onClose }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        trade: `${TRADES[0].code} — ${TRADES[0].title}`,
        message: "",
    });
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API_BASE}/contact`, {
                name: form.name,
                email: form.email,
                trade: form.trade,
                message: form.message,
            });
            toast.success("Request sent — we'll get back to you by email.");
            setForm({ name: "", email: "", trade: `${TRADES[0].code} — ${TRADES[0].title}`, message: "" });
            onClose();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not send. Please email us directly.",
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 32, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.97 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        data-testid="quote-dialog"
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#101725] p-6 md:p-8"
                    >
                        <button
                            onClick={onClose}
                            aria-label="Close dialog"
                            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-colors hover:text-white"
                        >
                            <X size={16} />
                        </button>
                        <p className="eyebrow">Quote request</p>
                        <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-white">
                            Tell us what needs work.
                        </h3>
                        <p className="mt-2 text-sm text-slate-400">
                            We reply by email to{" "}
                            <span className="text-slate-200">{EMAIL}</span>.
                        </p>

                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="field-label" htmlFor="q-name">Name</label>
                                    <input
                                        id="q-name"
                                        data-testid="quote-dialog-name-input"
                                        required
                                        minLength={2}
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your name"
                                        className="field-input"
                                    />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="q-email">Email</label>
                                    <input
                                        id="q-email"
                                        data-testid="quote-dialog-email-input"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="you@example.com"
                                        className="field-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="field-label" htmlFor="q-trade">Trade</label>
                                <select
                                    id="q-trade"
                                    data-testid="quote-dialog-trade-select"
                                    value={form.trade}
                                    onChange={set("trade")}
                                    className="field-input appearance-none"
                                >
                                    {TRADES.map((t) => (
                                        <option
                                            key={t.code}
                                            value={`${t.code} — ${t.title}`}
                                            className="bg-[#131B2A]"
                                        >
                                            {t.code} — {t.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="field-label" htmlFor="q-msg">Message</label>
                                <textarea
                                    id="q-msg"
                                    data-testid="quote-dialog-message-textarea"
                                    required
                                    minLength={5}
                                    rows={4}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Describe the job — location, property type, what needs doing."
                                    className="field-input resize-none"
                                />
                            </div>
                            <button
                                type="submit"
                                data-testid="quote-dialog-submit-button"
                                disabled={sending}
                                className="btn-primary w-full disabled:opacity-60"
                            >
                                {sending ? "Sending…" : "Send request"}
                            </button>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
